import { type CSSProperties, useMemo } from "react";
import {
  type ITableCell,
  type ITableSpecificSettings,
  TableHeaderPosition,
} from "../types";
import { getTableBorderRadiusValue } from "../utils/get-table-border-radius-value";
import {
  computeCellBorderOverlayStyle,
  type CellBorderSide,
} from "../utils/cell-border-overlay";

interface IProps
  extends Pick<
    ITableSpecificSettings,
    | "tableHasHeader"
    | "tableCols"
    | "tableRows"
    | "tableCellBorderColor"
    | "tableCellBorderWidth"
    | "tableCellBorderStyle"
    | "tableHasAlternatingRows"
    | "tableAlternatingRowsBorderColor"
    | "tableHeaderPosition"
    | "tableHeaderBorderColor"
    | "round"
  > {
  row: number;
  col: number;
  cell: ITableCell;
  isHeader: boolean;
  // True when the neighbour above/left actually draws the shared edge.
  topNeighborDrawsBottom: boolean;
  leftNeighborDrawsRight: boolean;
}

export const useTableCellBorders = ({
  isHeader,
  cell,
  col,
  row,
  tableHasHeader,
  tableCols,
  tableRows,
  tableCellBorderColor,
  tableCellBorderWidth,
  tableCellBorderStyle,
  tableHasAlternatingRows,
  tableAlternatingRowsBorderColor,
  tableHeaderPosition,
  tableHeaderBorderColor,
  round,
  topNeighborDrawsBottom,
  leftNeighborDrawsRight,
}: IProps) => {
  return useMemo(() => {
    const colSpan = cell.colSpan ?? 1;
    const rowSpan = cell.rowSpan ?? 1;
    const isFirstCol = col === 0;
    const isFirstRow = row === 0;
    const isLastCol = col + colSpan - 1 === tableCols - 1;
    const isLastRow = row + rowSpan - 1 === tableRows - 1;

    const getRadius = (when = true) => {
      return when ? getTableBorderRadiusValue(round) : 0;
    };

    const tdStyles = {
      borderTopLeftRadius: getRadius(isFirstCol && isFirstRow),
      borderBottomLeftRadius: getRadius(isFirstCol && isLastRow),
      borderTopRightRadius: getRadius(isLastCol && isFirstRow),
      borderBottomRightRadius: getRadius(isLastCol && isLastRow),
      borderTopColor: cell.borderTopColor ?? tableCellBorderColor,
      borderTopStyle: cell.borderTopStyle ?? tableCellBorderStyle,
      borderTopWidth: cell.borderTopWidth ?? tableCellBorderWidth,
      borderBottomColor: cell.borderBottomColor ?? tableCellBorderColor,
      borderBottomStyle: cell.borderBottomStyle ?? tableCellBorderStyle,
      borderBottomWidth: cell.borderBottomWidth ?? tableCellBorderWidth,
      borderLeftColor: cell.borderLeftColor ?? tableCellBorderColor,
      borderLeftStyle: cell.borderLeftStyle ?? tableCellBorderStyle,
      borderLeftWidth: cell.borderLeftWidth ?? tableCellBorderWidth,
      borderRightColor: cell.borderRightColor ?? tableCellBorderColor,
      borderRightStyle: cell.borderRightStyle ?? tableCellBorderStyle,
      borderRightWidth: cell.borderRightWidth ?? tableCellBorderWidth,
    } satisfies CSSProperties;

    let isAlt = false;
    let isTopAlt = false;
    let isBotAlt = false;
    if (tableHasAlternatingRows) {
      isAlt = row % 2 !== 0;
      isTopAlt = row > 0;
      isBotAlt = row < tableRows - 1 || isAlt;
    }

    if (isTopAlt) {
      tdStyles.borderTopColor = tableAlternatingRowsBorderColor;
    }
    if (isBotAlt) {
      tdStyles.borderBottomColor = tableAlternatingRowsBorderColor;
    }

    if (isAlt) {
      tdStyles.borderLeftColor = tableAlternatingRowsBorderColor;
      tdStyles.borderRightColor = tableAlternatingRowsBorderColor;
    }

    if (tableHasHeader) {
      if (tableHeaderPosition === TableHeaderPosition.Top) {
        if (row === 1) {
          tdStyles.borderTopColor = tableHeaderBorderColor;
        }
      } else if (tableHeaderPosition === TableHeaderPosition.Bottom) {
        if (row === tableRows - 2) {
          tdStyles.borderBottomColor = tableHeaderBorderColor;
        }
      } else if (tableHeaderPosition === TableHeaderPosition.Left) {
        if (col === 1) {
          tdStyles.borderLeftColor = tableHeaderBorderColor;
        }
      } else if (tableHeaderPosition === TableHeaderPosition.Right) {
        if (col === tableCols - 2) {
          tdStyles.borderRightColor = tableHeaderBorderColor;
        }
      }

      if (isHeader) {
        tdStyles.borderTopColor = tableHeaderBorderColor;
        tdStyles.borderBottomColor = tableHeaderBorderColor;
        tdStyles.borderLeftColor = tableHeaderBorderColor;
        tdStyles.borderRightColor = tableHeaderBorderColor;
      }
    }

    if (cell.borderTopColor) {
      tdStyles.borderTopColor = cell.borderTopColor;
    }
    if (cell.borderBottomColor) {
      tdStyles.borderBottomColor = cell.borderBottomColor;
    }
    if (cell.borderLeftColor) {
      tdStyles.borderLeftColor = cell.borderLeftColor;
    }
    if (cell.borderRightColor) {
      tdStyles.borderRightColor = cell.borderRightColor;
    }

    // Suppress own top/left border only when the neighbour draws the shared edge.
    // KEEP IN SYNC with editor-side table-borders-popover.tsx (drawn/facing).
    if (!isFirstRow && topNeighborDrawsBottom) {
      tdStyles.borderTopWidth = 0;
    }
    if (!isFirstCol && leftNeighborDrawsRight) {
      tdStyles.borderLeftWidth = 0;
    }

    const mkSide = (
      width: number | undefined,
      style: string | undefined,
      color: string | undefined,
    ): CellBorderSide => ({
      width: width ?? 0,
      style: style ?? "solid",
      color: color ?? "transparent",
      drawn: (width ?? 0) > 0,
    });
    const borderOverlay = computeCellBorderOverlayStyle({
      top: mkSide(
        tdStyles.borderTopWidth,
        tdStyles.borderTopStyle,
        tdStyles.borderTopColor,
      ),
      right: mkSide(
        tdStyles.borderRightWidth,
        tdStyles.borderRightStyle,
        tdStyles.borderRightColor,
      ),
      bottom: mkSide(
        tdStyles.borderBottomWidth,
        tdStyles.borderBottomStyle,
        tdStyles.borderBottomColor,
      ),
      left: mkSide(
        tdStyles.borderLeftWidth,
        tdStyles.borderLeftStyle,
        tdStyles.borderLeftColor,
      ),
      radius: {
        topLeft: Number(tdStyles.borderTopLeftRadius) || 0,
        topRight: Number(tdStyles.borderTopRightRadius) || 0,
        bottomLeft: Number(tdStyles.borderBottomLeftRadius) || 0,
        bottomRight: Number(tdStyles.borderBottomRightRadius) || 0,
      },
    });

    tdStyles.borderTopWidth = 0;
    tdStyles.borderRightWidth = 0;
    tdStyles.borderBottomWidth = 0;
    tdStyles.borderLeftWidth = 0;

    return {
      tdStyles,
      cornerBorder: null,
      borderOverlay,
    };
  }, [
    tableCellBorderColor,
    tableCellBorderStyle,
    tableCellBorderWidth,
    tableHasAlternatingRows,
    tableAlternatingRowsBorderColor,
    tableHeaderPosition,
    tableHeaderBorderColor,
    tableHasHeader,
    isHeader,
    cell,
    tableCols,
    tableRows,
    row,
    col,
    round,
    topNeighborDrawsBottom,
    leftNeighborDrawsRight,
  ]);
};
